import React from "react";
import { Box, MenuItem } from "@mui/material";
import dayjs from 'dayjs';
import SelectorTextField from "../../../components/SelectorTextField";
import TextField from "../../../components/TextField";
import CustomDatePicker from "../../../components/DatePicker";
import { albertaToday } from "../../../utils/albertaTime";
import {
    COLORS, FIELD_SX, REQUIRED_FIELD_MESSAGE, SECTION_BODY_SX, SECTION_TITLE_SX, COLUMN_SX, YES_OR_NO,
    columnWithLeftBorderSx, isYes, sectionContainerSx,
} from "./pageFields";
import {
    DISCLOSURE_STATUS_LABEL, PACKAGE_REQUEST_LABEL, OTHER_EXPLANATION_LABEL, OTHER_EXPLANATION_PLACEHOLDER,
    OTHER_EXPLANATION_MAX_LENGTH, isOther,
} from "./disclosureInformation";

const DISCLOSURE_INFORMATION_TITLE = "Disclosure Information";
// The explanation stops at the column's length. A field that passes its own input attributes replaces the text
// field's, so it keeps autofill off itself, as every other text field of the page has it.
const OTHER_EXPLANATION_INPUT_PROPS = { maxLength: OTHER_EXPLANATION_MAX_LENGTH, autoComplete: "off" };

/**
 * One of the three disclosure questions, Yes or No. They sit in Lawyer Information on the legacy layout and in the
 * Disclosure Information section on the new one, so the page draws them with this too.
 */
export function YesOrNoQuestion({ question, disabled }) {
    return (
        <Box sx={FIELD_SX}>
            <SelectorTextField label={question.label} placeholder={question.placeholder} required={true}
                select
                error={question.error ? true : null}
                disabled={disabled ? true : null}
                value={question.answer ? question.answer.label : ""}
                handleChange={question.onChange}
                helperText={question.error ? REQUIRED_FIELD_MESSAGE : null}>
                {YES_OR_NO.map((type) => (
                    <MenuItem key={type.selectionItemId} value={type.label}>
                        {type.label}
                    </MenuItem>))}
            </SelectorTextField>
        </Box>
    );
}

// Stories 11030/11031: a date sits under its question while the answer is Yes. Its calendar offers no day after
// Alberta's today, wherever the browser is.
function DisclosureDateField({ date, disabled }) {
    return (
        <Box sx={FIELD_SX}>
            <CustomDatePicker label={date.label} placeholder={date.label} required={true}
                style={{ width: "100%", backgroundColor: disabled ? COLORS.DISABLED_BACKGROUND : "" }}
                disabled={disabled ? true : null}
                maxDate={dayjs(albertaToday())}
                value={date.date}
                handleOnChange={date.onChange}
                error={date.error ? true : null}
                helperText={date.error}
            />
        </Box>
    );
}

/**
 * The Disclosure Information section (Feature 8459): four columns, the dates and the explanation beneath the field
 * they belong to, as the stories order them. `disclosure` is the state from useDisclosureInformation; `isLocked`
 * says nothing on the page can be changed (marked for review, inactive, or read-only for this user). Every field
 * of the section, the package request included, is editable by any role that may edit the page (story 11032 as
 * revised on 21 October 2026; until then a saved package request was locked for all but the two lead roles).
 */
export default function DisclosureInformationSection({ disclosure, isLocked }) {
    // A held Disclosure Status the metadata no longer offers: retired since it was chosen
    const { disclosureStatus } = disclosure;
    const holdsARetiredDisclosureStatus = disclosureStatus !== null && !disclosure.statusChoices.some((choice) => choice.id === disclosureStatus.id);

    return (
        <Box sx={sectionContainerSx(isLocked)} data-testid="disclosure-information-section">
            <Box sx={SECTION_TITLE_SX}>{DISCLOSURE_INFORMATION_TITLE}</Box>

            <Box sx={SECTION_BODY_SX}>
                {/* ═══ Column 1: Permission to order disclosure? ═══ */}
                <Box sx={COLUMN_SX}>
                    <YesOrNoQuestion question={disclosure.permission} disabled={isLocked} />
                </Box>

                {/* ═══ Column 2: Disclosure has been ordered?, Disclosure Order Date (while ordered = Yes) ═══ */}
                <Box sx={columnWithLeftBorderSx(true)}>
                    <YesOrNoQuestion question={disclosure.ordered} disabled={isLocked} />
                    {isYes(disclosure.ordered.answer) && <DisclosureDateField date={disclosure.orderDate} disabled={isLocked} />}
                </Box>

                {/* ═══ Column 3: Disclosure Uploaded?, Disclosure Upload Date (while uploaded = Yes) ═══ */}
                <Box sx={columnWithLeftBorderSx(false)}>
                    <YesOrNoQuestion question={disclosure.uploaded} disabled={isLocked} />
                    {isYes(disclosure.uploaded.answer) && <DisclosureDateField date={disclosure.uploadDate} disabled={isLocked} />}
                </Box>

                {/* ═══ Column 4: Disclosure Status, Please specify (while the status is Other), Disclosure Package Request ═══ */}
                <Box sx={columnWithLeftBorderSx(true)}>
                    <Box sx={FIELD_SX}>
                        <SelectorTextField label={DISCLOSURE_STATUS_LABEL} placeholder={DISCLOSURE_STATUS_LABEL} required={isYes(disclosure.ordered.answer)}
                            select
                            error={disclosure.disclosureStatusError ? true : null}
                            disabled={isLocked ? true : null}
                            value={disclosureStatus ? disclosureStatus.label : ""}
                            handleChange={disclosure.onDisclosureStatusChanged}
                            helperText={disclosure.disclosureStatusError}>
                            {disclosure.statusChoices.map((choice) => (
                                <MenuItem key={choice.id} value={choice.label}>
                                    {choice.label}
                                </MenuItem>))}
                            {holdsARetiredDisclosureStatus && (
                                <MenuItem key={disclosureStatus.id} value={disclosureStatus.label} disabled>
                                    {disclosureStatus.label}
                                </MenuItem>
                            )}
                        </SelectorTextField>
                    </Box>
                    {isOther(disclosureStatus) && (
                        <Box sx={FIELD_SX}>
                            <TextField label={OTHER_EXPLANATION_LABEL} placeholder={OTHER_EXPLANATION_PLACEHOLDER} required={true}
                                style={{ backgroundColor: isLocked ? COLORS.DISABLED_BACKGROUND : "" }}
                                disabled={isLocked ? true : null}
                                inputProps={OTHER_EXPLANATION_INPUT_PROPS}
                                error={disclosure.otherExplanationError ? true : null}
                                value={disclosure.otherExplanation}
                                handleChange={disclosure.onOtherExplanationChanged}
                                helperText={disclosure.otherExplanationError}
                            />
                        </Box>
                    )}
                    <Box sx={FIELD_SX}>
                        <SelectorTextField label={PACKAGE_REQUEST_LABEL} placeholder={PACKAGE_REQUEST_LABEL} required={isYes(disclosure.ordered.answer)}
                            select
                            error={disclosure.packageRequestError ? true : null}
                            disabled={isLocked ? true : null}
                            value={disclosure.packageRequest ? disclosure.packageRequest.label : ""}
                            handleChange={disclosure.onPackageRequestChanged}
                            helperText={disclosure.packageRequestError}>
                            {disclosure.packageRequestChoices.map((choice) => (
                                <MenuItem key={choice.value} value={choice.label}>
                                    {choice.label}
                                </MenuItem>))}
                        </SelectorTextField>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
